import React, { useState, useCallback, useEffect, forwardRef, useImperativeHandle } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Play, Square, UserPlus, Users, Building, AlertTriangle, Database, Clock } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { TestResult, TestLog } from "./TestingComponent";
import { loadRealUserData, type RealDataContext } from "@/utils/realDataLoader";
import { 
  generateBusinessEmail, 
  generatePassword, 
  generateUserProfile, 
  generateCompanyName,
  generateIndustryScenario,
  type UserProfile,
  type IndustryScenario 
} from "@/utils/realisticDataGenerators";

interface AuthenticationTestSuiteProps {
  onTestResult: (result: TestResult) => void;
  onTestLog: (log: Omit<TestLog, 'id' | 'timestamp'>) => void;
  isRunning: boolean;
  setIsRunning: (running: boolean) => void;
}

export interface AuthenticationTestSuiteRef {
  runAllTests: () => Promise<void>;
}

export const AuthenticationTestSuite = forwardRef<AuthenticationTestSuiteRef, AuthenticationTestSuiteProps>(({
  onTestResult,
  onTestLog,
  isRunning,
  setIsRunning,
}, ref) => {
  const [testType, setTestType] = useState<'agency' | 'business_owner' | 'both'>('both');
  const [testEmail, setTestEmail] = useState<string>('test@example.com');
  const [testPassword, setTestPassword] = useState<string>('TestPassword123!');
  const [currentTest, setCurrentTest] = useState<string>('');
  const [realData, setRealData] = useState<RealDataContext | null>(null);
  const [loadingData, setLoadingData] = useState<boolean>(false);
  const [industryScenarios, setIndustryScenarios] = useState<IndustryScenario[]>([]);

  // Expose runAllTests to parent via ref - will be assigned after runAllTests is defined
  const runAllTestsRef = React.useRef<(() => Promise<void>) | null>(null);
  
  useImperativeHandle(ref, () => ({
    runAllTests: async () => {
      if (runAllTestsRef.current) {
        await runAllTestsRef.current();
      }
    }
  }), []);

  const log = useCallback((level: TestLog['level'], message: string, details?: any, testId?: string) => {
    onTestLog({ level, message, details, testId });
  }, [onTestLog]);

  // Load real user data on component mount to provide context
  useEffect(() => {
    const loadData = async () => {
      setLoadingData(true);
      const data = await loadRealUserData();
      setRealData(data);
      setLoadingData(false);
      
      if (data) {
        // Update test email to use tenant-specific format with realistic business domain
        const businessEmail = generateBusinessEmail('test', 'user', 'technology');
        setTestEmail(businessEmail);
        
        // Generate industry-specific test scenarios
        const industries = ['real_estate', 'automotive', 'healthcare', 'ecommerce', 'professional'];
        const scenarios = industries.map(industry => generateIndustryScenario(industry));
        setIndustryScenarios(scenarios);
      }
    };
    loadData();
  }, []);

  const createTestResult = useCallback((
    testName: string,
    status: TestResult['status'],
    details: string,
    errorMessage?: string,
    debugData?: any,
    startTime?: Date,
    endTime?: Date
  ): TestResult => {
    const start = startTime || new Date();
    const end = endTime || new Date();
    const duration = endTime && startTime ? end.getTime() - start.getTime() : 0;
    
    return {
      id: `test_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
      testName,
      name: testName, // For backward compatibility
      status,
      startTime: start,
      endTime: status !== 'running' ? end : undefined,
      duration: status !== 'running' ? duration : undefined,
      details,
      error: errorMessage, // For testing utils compatibility
      errorMessage,
      debugData,
      category: "authentication"
    };
  }, []);

  // Generate unique test email to avoid conflicts, using realistic business email formats
  const generateTestEmail = useCallback((role: string) => {
    const timestamp = Date.now();
    const tenantSuffix = realData ? `-${realData.tenant.tenant_id.slice(0, 8)}` : '';
    
    // Use realistic business email format for the role
    if (industryScenarios.length > 0) {
      const randomScenario = industryScenarios[Math.floor(Math.random() * industryScenarios.length)];
      const baseEmail = randomScenario.companyProfile.email;
      const [localPart, domain] = baseEmail.split('@');
      return `test-${role}-${timestamp}${tenantSuffix}@${domain}`;
    }
    
    // Fallback to realistic business domain
    return generateBusinessEmail(`test-${role}`, `user${timestamp}`, 'technology');
  }, [realData, industryScenarios]);

  // Test user signup with different roles
  const testUserSignup = useCallback(async (role: 'agency' | 'business_owner') => {
    const testName = `User Signup - ${role}`;
    const testEmail = generateTestEmail(role);
    const startTime = new Date();
    
    try {
      console.log(`[AUTH] INFO: Testing ${role} signup flow...`, { testEmail });
      log('info', `Testing ${role} signup flow...`, { testEmail });
      
      // Test signup
      const { data: signupData, error: signupError } = await supabase.auth.signUp({
        email: testEmail,
        password: testPassword,
        options: {
          data: {
            role: role,
            // Add realistic user metadata from industry scenarios
            firstName: industryScenarios.length > 0 ? industryScenarios[0].companyProfile.firstName : 'Test',
            lastName: industryScenarios.length > 0 ? industryScenarios[0].companyProfile.lastName : 'User',
            company: industryScenarios.length > 0 ? industryScenarios[0].companyProfile.company : generateCompanyName(),
            industry: industryScenarios.length > 0 ? industryScenarios[0].industry : 'technology'
          },
          emailRedirectTo: `${window.location.origin}/`
        }
      });

      if (signupError) {
        throw new Error(`Signup failed: ${signupError.message}`);
      }

      if (!signupData.user) {
        throw new Error('No user returned from signup');
      }

      console.log(`[AUTH] SUCCESS: Successfully created ${role} user`, { 
        userId: signupData.user.id, 
        email: signupData.user.email 
      });
      log('success', `Successfully created ${role} user`, { 
        userId: signupData.user.id, 
        email: signupData.user.email 
      });

      // Wait a moment for triggers to process
      await new Promise(resolve => setTimeout(resolve, 2000));

      // Verify user profile was created
      const { data: userData, error: userError } = await supabase
        .from('calie_users')
        .select('*')
        .eq('user_id', signupData.user.id)
        .single();

      if (userError || !userData) {
        throw new Error(`User profile not created: ${userError?.message || 'No data'}`);
      }

      console.log('[AUTH] SUCCESS: User profile created successfully', userData);
      log('success', 'User profile created successfully', userData);

      // Verify tenant was created
      const { data: tenantData, error: tenantError } = await supabase
        .from('calie_tenants')
        .select('*')
        .eq('tenant_id', userData.tenant_id)
        .single();

      if (tenantError || !tenantData) {
        throw new Error(`Tenant not created: ${tenantError?.message || 'No data'}`);
      }

      console.log('[AUTH] SUCCESS: Tenant created successfully', tenantData);
      log('success', 'Tenant created successfully', tenantData);

      // Verify membership was created
      const { data: membershipData, error: membershipError } = await supabase
        .from('calie_tenant_members')
        .select('*')
        .eq('user_id', signupData.user.id)
        .eq('tenant_id', userData.tenant_id)
        .single();

      if (membershipError || !membershipData) {
        throw new Error(`Membership not created: ${membershipError?.message || 'No data'}`);
      }

      console.log('[AUTH] SUCCESS: Membership created successfully', membershipData);
      log('success', 'Membership created successfully', membershipData);

      // CRITICAL: Verify onboarding state was initialized
      const { data: onboardingData, error: onboardingError } = await supabase
        .from('calie_onboarding_state')
        .select('*')
        .eq('tenant_id', userData.tenant_id)
        .single();

      if (onboardingError || !onboardingData) {
        throw new Error(`Onboarding state not initialized: ${onboardingError?.message || 'No data'}`);
      }

      console.log('[AUTH] SUCCESS: Onboarding state initialized successfully', onboardingData);
      log('success', 'Onboarding state initialized successfully', onboardingData);

      // Verify database relationships
      if (membershipData.tenant_id !== userData.tenant_id) {
        throw new Error('Database relationship inconsistency: membership tenant_id does not match user tenant_id');
      }

      if (membershipData.user_id !== signupData.user.id) {
        throw new Error('Database relationship inconsistency: membership user_id does not match signup user_id');
      }

      console.log('[AUTH] SUCCESS: Database relationships verified successfully');
      log('success', 'Database relationships verified successfully');

      // Clean up test user
      try {
        await supabase.auth.signOut();
        console.log('[AUTH] INFO: Signed out test user');
        log('info', 'Signed out test user');
      } catch (cleanupError) {
        console.log('[AUTH] WARN: Failed to sign out test user', cleanupError);
        log('warn', 'Failed to sign out test user', cleanupError);
      }

      const endTime = new Date();
      const result = createTestResult(
        testName, 
        'success', 
        `Successfully created ${role} user with profile, tenant, membership, and onboarding state`,
        undefined,
        { 
          userId: signupData.user.id, 
          tenantId: userData.tenant_id,
          membershipId: membershipData.user_id,
          onboardingInitialized: true
        },
        startTime,
        endTime
      );
      onTestResult(result);

    } catch (error: any) {
      console.log(`[AUTH] ERROR: ${testName} failed`, error);
      log('error', `${testName} failed`, error);
      const endTime = new Date();
      const result = createTestResult(testName, 'failure', '', error.message, { error: error.message }, startTime, endTime);
      onTestResult(result);
    }
  }, [log, createTestResult, onTestResult, testPassword, generateTestEmail]);

  // Test session persistence
  const testSessionPersistence = useCallback(async () => {
    const testName = "Session Persistence";
    const testEmail = generateTestEmail('session-test');
    const startTime = new Date();
    
    try {
      console.log('[AUTH] INFO: Testing session persistence...', { testEmail });
      log('info', 'Testing session persistence...', { testEmail });

      // Create test user
      const { data: signupData, error: signupError } = await supabase.auth.signUp({
        email: testEmail,
        password: testPassword,
        options: {
          emailRedirectTo: `${window.location.origin}/`
        }
      });

      if (signupError || !signupData.user) {
        throw new Error(`Failed to create test user: ${signupError?.message}`);
      }

      // Test getting session
      const { data: sessionData, error: sessionError } = await supabase.auth.getSession();
      
      if (sessionError || !sessionData.session) {
        throw new Error(`Session not found: ${sessionError?.message}`);
      }

      console.log('[AUTH] SUCCESS: Session retrieved successfully', { 
        userId: sessionData.session.user.id,
        expiresAt: sessionData.session.expires_at 
      });
      log('success', 'Session retrieved successfully', { 
        userId: sessionData.session.user.id,
        expiresAt: sessionData.session.expires_at 
      });

      // Test token refresh
      const { data: refreshData, error: refreshError } = await supabase.auth.refreshSession();
      
      if (refreshError) {
        console.log('[AUTH] WARN: Token refresh failed', refreshError);
        log('warn', 'Token refresh failed', refreshError);
      } else {
        console.log('[AUTH] SUCCESS: Token refreshed successfully');
        log('success', 'Token refreshed successfully');
      }

      // Clean up
      await supabase.auth.signOut();

      const endTime = new Date();
      const result = createTestResult(
        testName, 
        'success', 
        'Session persistence and token refresh working correctly',
        undefined,
        { 
          sessionValid: true, 
          tokenRefreshWorking: !refreshError,
          sessionData: { userId: sessionData.session.user.id, expiresAt: sessionData.session.expires_at }
        },
        startTime,
        endTime
      );
      onTestResult(result);

    } catch (error: any) {
      console.log(`[AUTH] ERROR: ${testName} failed`, error);
      log('error', `${testName} failed`, error);
      const endTime = new Date();
      const result = createTestResult(testName, 'failure', '', error.message, { error: error.message }, startTime, endTime);
      onTestResult(result);
    }
  }, [log, createTestResult, onTestResult, testPassword, generateTestEmail]);

  // Test duplicate account handling
  const testDuplicateAccountHandling = useCallback(async () => {
    const testName = "Duplicate Account Handling";
    const testEmail = generateTestEmail('duplicate-test');
    const startTime = new Date();
    
    try {
      console.log('[AUTH] INFO: Testing duplicate account handling...', { testEmail });
      log('info', 'Testing duplicate account handling...', { testEmail });

      // First signup
      const { data: firstSignup, error: firstError } = await supabase.auth.signUp({
        email: testEmail,
        password: testPassword,
        options: {
          emailRedirectTo: `${window.location.origin}/`
        }
      });

      if (firstError) {
        throw new Error(`First signup failed: ${firstError.message}`);
      }

      console.log('[AUTH] INFO: First signup successful');
      log('info', 'First signup successful');

      // Attempt duplicate signup
      const { data: duplicateSignup, error: duplicateError } = await supabase.auth.signUp({
        email: testEmail,
        password: testPassword,
        options: {
          emailRedirectTo: `${window.location.origin}/`
        }
      });

      // Should either return existing user or appropriate error
      if (duplicateError) {
        console.log('[AUTH] SUCCESS: Duplicate signup properly rejected', { error: duplicateError.message });
        log('success', 'Duplicate signup properly rejected', { error: duplicateError.message });
      } else {
        console.log('[AUTH] SUCCESS: Duplicate signup handled gracefully', duplicateSignup);
        log('success', 'Duplicate signup handled gracefully', duplicateSignup);
      }

      // Clean up
      await supabase.auth.signOut();

      const endTime = new Date();
      const result = createTestResult(
        testName, 
        'success', 
        'Duplicate account handling working correctly',
        undefined,
        { 
          firstSignupSuccessful: !firstError,
          duplicateHandled: true,
          duplicateError: duplicateError?.message || 'No error - handled gracefully'
        },
        startTime,
        endTime
      );
      onTestResult(result);

    } catch (error: any) {
      console.log(`[AUTH] ERROR: ${testName} failed`, error);
      log('error', `${testName} failed`, error);
      const endTime = new Date();
      const result = createTestResult(testName, 'failure', '', error.message, { error: error.message }, startTime, endTime);
      onTestResult(result);
    }
  }, [log, createTestResult, onTestResult, testPassword, generateTestEmail]);

  // Test input validation
  const testSignupValidation = useCallback(async () => {
    const testName = "Signup Input Validation";
    const startTime = new Date();
    
    try {
      console.log('[AUTH] INFO: Testing signup input validation...');
      log('info', 'Testing signup input validation...');

      // Test invalid email
      const { error: emailError } = await supabase.auth.signUp({
        email: 'invalid-email',
        password: testPassword
      });

      if (emailError) {
        console.log('[AUTH] SUCCESS: Invalid email properly rejected', { error: emailError.message });
        log('success', 'Invalid email properly rejected', { error: emailError.message });
      } else {
        throw new Error('Invalid email was not rejected');
      }

      // Test weak password using generated weak password
      const weakPassword = generatePassword('weak');
      const { error: passwordError } = await supabase.auth.signUp({
        email: generateTestEmail('weak-password'),
        password: weakPassword
      });
      
      // Also test medium and strong password validation
      const mediumPassword = generatePassword('medium');
      const { error: mediumPasswordError } = await supabase.auth.signUp({
        email: generateTestEmail('medium-password'),
        password: mediumPassword
      });
      
      const strongPassword = generatePassword('strong');
      const { error: strongPasswordError } = await supabase.auth.signUp({
        email: generateTestEmail('strong-password'),
        password: strongPassword
      });

      if (passwordError) {
        console.log('[AUTH] SUCCESS: Weak password properly rejected', { error: passwordError.message });
        log('success', 'Weak password properly rejected', { error: passwordError.message, password: weakPassword });
      } else {
        console.log('[AUTH] WARN: Weak password was accepted - may need stronger validation');
        log('warn', 'Weak password was accepted - may need stronger validation', { password: weakPassword });
      }
      
      // Log results for different password strengths
      if (mediumPasswordError) {
        log('info', 'Medium password validation result', { error: mediumPasswordError.message, password: 'medium complexity' });
      } else {
        log('success', 'Medium password accepted', { password: 'medium complexity' });
      }
      
      if (strongPasswordError) {
        log('info', 'Strong password validation result', { error: strongPasswordError.message, password: 'strong complexity' });
      } else {
        log('success', 'Strong password accepted', { password: 'strong complexity' });
      }

      const endTime = new Date();
      const result = createTestResult(
        testName, 
        'success', 
        'Input validation working correctly',
        undefined,
        { 
          emailValidationWorking: !!emailError,
          passwordValidationWorking: {
            weak: !!passwordError,
            medium: !!mediumPasswordError,
            strong: !!strongPasswordError
          },
          emailError: emailError?.message,
          passwordErrors: {
            weak: passwordError?.message,
            medium: mediumPasswordError?.message,
            strong: strongPasswordError?.message
          },
          passwordSamples: {
            weak: weakPassword,
            medium: 'medium complexity sample',
            strong: 'strong complexity sample'
          }
        },
        startTime,
        endTime
      );
      onTestResult(result);

    } catch (error: any) {
      console.log(`[AUTH] ERROR: ${testName} failed`, error);
      log('error', `${testName} failed`, error);
      const endTime = new Date();
      const result = createTestResult(testName, 'failure', '', error.message, { error: error.message }, startTime, endTime);
      onTestResult(result);
    }
  }, [log, createTestResult, onTestResult, testPassword, generateTestEmail]);

  // Run all authentication tests
  const runAllTests = useCallback(async () => {
    if (isRunning) return;
    
    setIsRunning(true);
    setCurrentTest('Authentication Tests');
    
    try {
      console.log('[AUTH] INFO: Starting comprehensive authentication test suite...');
      log('info', 'Starting comprehensive authentication test suite...');

      // Test signup validation first
      await testSignupValidation();
      await new Promise(resolve => setTimeout(resolve, 1000));

      // Test session persistence
      await testSessionPersistence();
      await new Promise(resolve => setTimeout(resolve, 1000));

      // Test duplicate account handling
      await testDuplicateAccountHandling();
      await new Promise(resolve => setTimeout(resolve, 1000));

      // Test role-specific signups
      if (testType === 'agency' || testType === 'both') {
        await testUserSignup('agency');
        await new Promise(resolve => setTimeout(resolve, 1000));
      }

      if (testType === 'business_owner' || testType === 'both') {
        await testUserSignup('business_owner');
        await new Promise(resolve => setTimeout(resolve, 1000));
      }

      console.log('[AUTH] SUCCESS: Authentication test suite completed!');
      log('success', 'Authentication test suite completed!');

    } catch (error: any) {
      console.log('[AUTH] ERROR: Authentication test suite failed', error);
      log('error', 'Authentication test suite failed', error);
    } finally {
      setIsRunning(false);
      setCurrentTest('');
    }
  }, [isRunning, setIsRunning, log, testType, testUserSignup, testSessionPersistence, testDuplicateAccountHandling, testSignupValidation]);

  // Assign runAllTests to ref for imperative handle
  React.useEffect(() => {
    runAllTestsRef.current = runAllTests;
  }, [runAllTests]);

  const stopTests = useCallback(() => {
    setIsRunning(false);
    setCurrentTest('');
    console.log('[AUTH] WARN: Authentication tests stopped by user');
    log('warn', 'Authentication tests stopped by user');
  }, [setIsRunning, log]);

  return (
    <div className="space-y-4">
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <UserPlus className="h-5 w-5" />
            Authentication & Signup Flow Testing
          </CardTitle>
          <CardDescription>
            Test user registration, role assignment, session management, and authentication flows
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* Test Configuration */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="space-y-2">
              <Label htmlFor="test-type">Test Type</Label>
              <Select value={testType} onValueChange={(value: any) => setTestType(value)}>
                <SelectTrigger>
                  <SelectValue placeholder="Select test type" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="agency">
                    <div className="flex items-center gap-2">
                      <Building className="h-4 w-4" />
                      Agency Only
                    </div>
                  </SelectItem>
                  <SelectItem value="business_owner">
                    <div className="flex items-center gap-2">
                      <Users className="h-4 w-4" />
                      Business Owner Only
                    </div>
                  </SelectItem>
                  <SelectItem value="both">
                    <div className="flex items-center gap-2">
                      <UserPlus className="h-4 w-4" />
                      Both Roles
                    </div>
                  </SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label htmlFor="test-email">Base Test Email</Label>
              <Input
                id="test-email"
                value={testEmail}
                onChange={(e) => setTestEmail(e.target.value)}
                placeholder="test@example.com"
                disabled={isRunning}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="test-password">Test Password</Label>
              <Input
                id="test-password"
                type="password"
                value={testPassword}
                onChange={(e) => setTestPassword(e.target.value)}
                placeholder="Enter test password"
                disabled={isRunning}
              />
            </div>
          </div>

          {/* Test Controls */}
          <div className="flex items-center gap-2">
            {!isRunning ? (
              <Button onClick={runAllTests} className="flex items-center gap-2">
                <Play className="h-4 w-4" />
                Run Authentication Tests
              </Button>
            ) : (
              <Button onClick={stopTests} variant="destructive" className="flex items-center gap-2">
                <Square className="h-4 w-4" />
                Stop Tests
              </Button>
            )}
          </div>

          {/* Current Test Status */}
          {isRunning && (
            <Card className="border-primary">
              <CardContent className="pt-4">
                <div className="flex items-center gap-2">
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-primary"></div>
                  <span className="text-sm font-medium">Running: {currentTest}</span>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Test Information */}
          <Card className="bg-muted/50">
            <CardHeader className="pb-3">
              <CardTitle className="text-sm flex items-center gap-2">
                <AlertTriangle className="h-4 w-4" />
                Authentication Test Suite
              </CardTitle>
            </CardHeader>
            <CardContent className="pt-0">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-sm">
                <div>
                  <strong>Tests Include:</strong>
                  <ul className="list-disc list-inside space-y-1 mt-1 text-muted-foreground">
                    <li>Email/Password Registration</li>
                    <li>Role-specific Signup (Agency/Business Owner)</li>
                    <li>Session Management & Persistence</li>
                    <li>Profile & Workspace Creation</li>
                  </ul>
                </div>
                <div>
                  <strong>Validation Tests:</strong>
                  <ul className="list-disc list-inside space-y-1 mt-1 text-muted-foreground">
                    <li>Input Validation (Email/Password)</li>
                    <li>Duplicate Account Handling</li>
                    <li>Database Trigger Validation</li>
                    <li>Token Refresh & Security</li>
                  </ul>
                </div>
              </div>
            </CardContent>
          </Card>
        </CardContent>
      </Card>
    </div>
  );
});

AuthenticationTestSuite.displayName = 'AuthenticationTestSuite';